import {
  accessibilityDataAtom,
  availableAuditEntriesAtom,
  currentAuditEntryAtom,
  currentAuditEntryIdAtom,
  notificationLastViewDateAtom,
  orgUsersAtom,
  todosDataAtom,
  userAtom,
  userNotificationsAtom,
  versionOfLastNotificationSeenAtom,
} from '@/global-store';
import { useFetchNotifications } from '@/http/queries';
import UserAvatar from '@/pages/report/MyGoals/components/UserAvatar';
import { todoCategoryProps } from '@/pages/report/MyGoals/components/utils';
import { BookmarkCategories } from '@helpers/constants';
import { getTimeDifferenceFromNow } from '@helpers/functions';
import {
  Avatar,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TabsList,
  Typography,
  TabsTrigger,
  Tooltip,
  TabsWrapper,
} from '@eye-able/ui';
import {
  IconBell,
  IconBellRingingFilled,
  IconMail,
  IconWorldWww,
} from '@tabler/icons-react';
import { differenceInDays } from 'date-fns';
import { useAtom } from 'jotai';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ICON_SIZE = '1.38rem';
function NotificationSection() {
  const [open, setOpen] = useState(false);
  const [versionOfLastNotificationSeen] = useAtom(
    versionOfLastNotificationSeenAtom,
  );
  const [notificationLastViewDate, setNotificationLastViewDate] = useAtom(
    notificationLastViewDateAtom,
  );
  const [orgUsers] = useAtom(orgUsersAtom);
  const [todosData] = useAtom(todosDataAtom);
  const { t } = useTranslation();
  const [accessibilityData] = useAtom(accessibilityDataAtom);
  const [userNotifications] = useAtom(userNotificationsAtom);
  const navigate = useNavigate();

  const triggerRef = useRef(null);

  const handleSubscription = () => {
    setOpen(false);
    navigate('/subscriptions');
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setNotificationLastViewDate(Date.now());
    }
    setOpen(nextOpen);
  };

  const titleTextByCategory = useCallback(
    function titleTextByCategoryCb(todo) {
      if (!todo) return t('noName');

      const { title, category, props, filterId } = todo;

      if (category === BookmarkCategories.BROKEN_LINKS) {
        return props?.url ?? title;
      } else if (
        [
          BookmarkCategories.PDFS_FOUND,
          BookmarkCategories.PDFS_UPLOAD,
        ].includes(category)
      )
        return filterId ?? props?.filterId ?? title;
      else if (
        [
          BookmarkCategories.ISSUE,
          BookmarkCategories.TOP_ISSUE,
          BookmarkCategories.SITE_ISSUE,
        ].includes(category)
      )
        return (
          accessibilityData?.eaRules[props?.ruleNum]?.content || t('noName')
        );
      else if (
        [
          BookmarkCategories.SITE,
          BookmarkCategories.SPELLCHECKER_PAGE,
        ].includes(category)
      )
        return (
          accessibilityData.report?.summary?.summaryPerPage[props?.url]
            ?.title || t('noName')
        );
      else if (category === BookmarkCategories.SPELLCHECKER_MISTAKE)
        return props.mistake || t('noName');

      return t('noName');
    },
    [accessibilityData, t],
  );

  const notifications = useMemo(() => {
    if (!accessibilityData) return [];

    const _notifications = [];

    userNotifications.forEach((activity) => {
      const todo = todosData?.todos?.[activity?.todoId];

      if (!todo) return;

      const modifierUser = activity?.modifier
        ? orgUsers?.find((user) => user.id === activity?.modifier)
        : undefined;
      const boardStatus = todosData?.boardConfig
        ? Object.keys(todosData?.boardConfig).find((status) => {
            return todosData?.boardConfig[status].includes(+activity?.todoId);
          })
        : 'backlog';
      const todoTitle = accessibilityData
        ? titleTextByCategory(todo)
        : t('noName');

      _notifications.push({
        ...activity,
        modifierUser,
        notificationProps: {
          todo: { ...todo, title: todoTitle, status: boardStatus },
        },
      });
    });

    return _notifications;
  }, [
    orgUsers,
    todosData,
    accessibilityData,
    t,
    titleTextByCategory,
    userNotifications,
  ]);

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      triggerRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const { data } = useFetchNotifications();

  const hasUserNotifications =
    userNotifications.filter(
      (notification) => notificationLastViewDate < notification.activityDate,
    ).length !== 0;

  const hasUpdates = versionOfLastNotificationSeen !== __APP_VERSION__;

  const hasNewNotification = hasUserNotifications || hasUpdates;

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup='true'
          ref={triggerRef}
        >
          {hasNewNotification ? (
            <IconBellRingingFilled
              aria-hidden
              size={ICON_SIZE}
              style={{
                animation: 'ringBell 1.4s ease-in-out infinite alternate',
                transformOrigin: '50% 90%',
              }}
            />
          ) : (
            <IconBell aria-hidden size={ICON_SIZE} />
          )}
          <span className='eye:max-xl:sr-only'>{t('notifications')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side='bottom'
        align='end'
        matchTriggerWidth={false}
        className='eye:p-0 eye:max-h-[calc(100vh-140px)] eye:md:max-h-[calc(100vh-120px)] eye:lg:max-h-[calc(100vh-60px)]'
      >
        <div className='eye:p-xs eye:bg-white eye:w-[calc(100vw-28px)] eye:sm:w-[430px]'>
          <Tabs
            notifications={notifications}
            orgUsers={orgUsers}
            data={data}
            hasUserNotifications={hasUserNotifications}
            hasUpdates={hasUpdates}
            versionOfLastNotificationSeen={versionOfLastNotificationSeen}
          />

          <Button
            type='button'
            onClick={handleSubscription}
            className='eye:w-full'
            variant='outline'
          >
            <IconMail aria-hidden />
            {t('subscriptions')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Tabs({
  notifications,
  orgUsers,
  data,
  hasUserNotifications,
  hasUpdates,
}) {
  const { t } = useTranslation();
  const [, setVersionOfLastNotificationSeen] = useAtom(
    versionOfLastNotificationSeenAtom,
  );
  const [activeTab, setActiveTab] = useState('0');

  const tabs = useMemo(() => {
    return [
      {
        title: t('notifications'),
        content: (
          <NotificationsTab notifications={notifications} orgUsers={orgUsers} />
        ),
        showDot: hasUserNotifications,
      },
      {
        title: t('updates'),
        content: <UpdatesTab data={data} />,
        showDot: hasUpdates,
        onClick: () => {
          setVersionOfLastNotificationSeen(__APP_VERSION__);
        },
      },
    ];
  }, [
    t,
    notifications,
    orgUsers,
    data,
    hasUserNotifications,
    hasUpdates,
    setVersionOfLastNotificationSeen,
  ]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    const selectedTab = tabs[Number(value)];
    if (selectedTab?.onClick) {
      selectedTab.onClick();
    }
  };

  return (
    <TabsWrapper value={activeTab} onValueChange={handleTabChange} tabs={tabs}>
      <TabsList className='eye:bg-grey-20 eye:gap-2 eye:mb-2 eye:w-full'>
        {tabs.map((tab, index) => (
          <TabsTrigger
            key={tab.title}
            value={String(index)}
            className='eye:text-black eye:flex-1 eye:justify-center'
          >
            <div className='eye:items-center eye:flex eye:gap-xs'>
              {tab.title}
              {tab.showDot === true && (
                <div className='eye:size-8 eye:bg-red-100 eye:rounded-full' />
              )}
            </div>
          </TabsTrigger>
        ))}
      </TabsList>
    </TabsWrapper>
  );
}

function UpdatesTab({ data }) {
  const { t } = useTranslation();
  return (
    <div className='eye:mt-xs eye:space-y-2xs eye:mb-2xs'>
      {data?.map((notification) => (
        <div
          key={notification.date + notification.version}
          className='eye:border eye:border-grey-100 eye:p-2xs eye:rounded-md'
        >
          <div className='eye:font-semibold eye:mb-3xs'>
            <div>
              {t('date')}: {notification.date}
            </div>
            <div>Version: {notification.version}</div>
          </div>

          <ul className='eye:list-disc eye:pl-xs'>
            {notification.updates?.map((update) => (
              <li key={update}>{update}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function NotificationsTab({ notifications }) {
  const { t, i18n } = useTranslation();
  const [user] = useAtom(userAtom);
  const lang = i18n.resolvedLanguage;
  const navigate = useNavigate();
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  const [, setCurrentAuditEntryId] = useAtom(currentAuditEntryIdAtom);
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  const handleClicked = (activity) => {
    const todoDomain = activity.notificationProps.todo.domain;
    if (currentAuditEntry?.domain !== todoDomain) {
      setCurrentAuditEntryId(
        availableAuditEntries.find((entry) => entry.domain === todoDomain)?.id,
      );
    }
    navigate('/report/my-goals', {
      state: { todoId: activity?.todoId, openDetail: true },
    });
  };

  const notificationTitlesMap = {
    4: t('assignedYouTodo'),
    6: t('addedACommentNotifi'),
    8: t('changedDescriptionNotifi'),
  };

  return notifications.length ? (
    <div className='eye:py-xs eye:flex eye:flex-col eye:gap-xs'>
      {notifications
        ?.sort((a, b) => b.activityDate - a.activityDate)
        .map((activity, index) => {
          const { modifierUser, notificationProps } = activity;

          const { icon: BookmarkIcon } =
            todoCategoryProps[notificationProps?.todo?.category];

          const timeDifference = getTimeDifferenceFromNow(
            new Date(activity.activityDate),
            lang,
          );
          const datesDifference = differenceInDays(
            new Date(),
            new Date(activity.activityDate),
          );
          const locale = lang === 'de' ? 'de-DE' : 'en-US';
          const activityDateStr =
            datesDifference < 2
              ? timeDifference
              : new Date(activity.activityDate).toLocaleString(locale, {
                  dateStyle: 'full',
                  timeStyle: 'medium',
                });

          return (
            <Fragment key={`${activity.type}-${index}`}>
              <button
                type='button'
                className='eye:border eye:border-grey-20 eye:rounded-md eye:items-start eye:gap-2xs eye:p-2xs eye:flex eye:hover:outline-1 eye:cursor-pointer eye:text-start'
                onClick={() => handleClicked(activity)}
              >
                {modifierUser ? (
                  <UserAvatar
                    hideName
                    user={modifierUser}
                    me={+modifierUser.id === +user.id}
                  />
                ) : (
                  <Avatar
                    alt='Eye-Able'
                    src='https://assets.eye-able.com/dashboard-assets/icons/Icon_Dashboard_eye-able_black.svg'
                  />
                )}
                <div className='eye:flex eye:flex-col eye:gap-4xs eye:min-w-0'>
                  <Typography
                    variant='body-s-highlight'
                    className='eye:wrap-break-word'
                  >
                    {`${modifierUser.surname} ${modifierUser.name} ${notificationTitlesMap[activity.type]} `}
                  </Typography>
                  <div className='eye:flex eye:gap-4xs eye:items-start eye:min-w-0'>
                    <BookmarkIcon aria-hidden className='eye:shrink-0' />
                    <Typography variant='body-s'>
                      {notificationProps?.todo?.title}
                    </Typography>
                  </div>
                  <div className='eye:flex eye:gap-4xs eye:items-start eye:min-w-0'>
                    <IconWorldWww aria-hidden className='eye:shrink-0' />
                    <Tooltip
                      content={notificationProps?.todo?.domain}
                      className='eye:max-w-[350px]'
                    >
                      <Typography variant='body-s' noWrap>
                        {notificationProps?.todo?.domain}
                      </Typography>
                    </Tooltip>
                  </div>
                  <Typography
                    variant='body-s'
                    className='eye:text-grey-disabled!'
                  >
                    {activityDateStr}
                  </Typography>
                </div>
              </button>
            </Fragment>
          );
        })}
    </div>
  ) : (
    <Typography className='eye:py-m eye:text-center'>
      {t('noNewNotifications')}
    </Typography>
  );
}

export default NotificationSection;
